import React, { useEffect, useState } from "react";
import axios from "../../auth/auth";
import { RiFileCheckFill } from "react-icons/ri";
import { RiZoomInLine, RiArrowLeftLine } from "react-icons/ri";
import { FiMail, FiEye } from "react-icons/fi";


/* ---------------- CONSTANTS ---------------- */

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PARKING_CONCEPT_ID = "2980";
const COLONIAL_PARKING_ID = "6724";
const CITY_PARKING_ID = "26380";

const GARAGE_REQUIRED_CUSTOMERS = [
  PARKING_CONCEPT_ID,
  COLONIAL_PARKING_ID,
  CITY_PARKING_ID,
];

const PARKING_CONCEPT_GROUP_1 = ["125", "142", "309", "428", "33", "153", "156"];

/* ---------------- COMPONENT ---------------- */

const ApprovedInvoicesTab = () => {
  const [customers, setCustomers] = useState([]);
  const [months, setMonths] = useState([]);
  const [statusMonths, setStatusMonths] = useState([]);
  const [allStatusMonths, setAllStatusMonths] = useState([]); // Keep all months for year dropdown
  const [garages, setGarages] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedGarages, setSelectedGarages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [showCompareSelector, setShowCompareSelector] = useState(false);
  const [compareMonth, setCompareMonth] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [statusFilterMonth, setStatusFilterMonth] = useState("");
  const [statusFilterYear, setStatusFilterYear] = useState("");
  const [showStatusTable, setShowStatusTable] = useState(false);

  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [representativeInfo, setRepresentativeInfo] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);


  /* ---------------- MONTH SORT ---------------- */

  const sortMonthsDescending = (monthsArray) => {
    return [...monthsArray].sort((a, b) => {
      const [monthA, yearA] = a.split("-").map(Number);
      const [monthB, yearB] = b.split("-").map(Number);

      const dateA = new Date(yearA, monthA - 1);
      const dateB = new Date(yearB, monthB - 1);

      return dateB - dateA;
    });
  };


  // 01-2026 → January 2026
  const formatMonthYear = (monthStr) => {
    if (!monthStr) return "";

    const date = new Date(monthStr);

    return date.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  };

  /* ---------------- GROUP LOGIC ---------------- */

  const isParkingConcept =
    String(selectedCustomer) === PARKING_CONCEPT_ID;

  const isGroup1Garage = (garageId) =>
    PARKING_CONCEPT_GROUP_1.includes(String(garageId));

  const hasGroup1Selected = selectedGarages.some((id) =>
    isGroup1Garage(id)
  );

  const hasGroup2Selected = selectedGarages.some(
    (id) => !isGroup1Garage(id)
  );

  /* ---------------- LOADERS ---------------- */

  const loadCustomers = async () => {
    const res = await axios.get(
      "/invoice/customers/"
    );
    setCustomers(res.data);
  };

  const loadMonths = async (customerID) => {
    const res = await axios.get(
      `/invoice/months/?customer_id=${customerID}`
    );

    const sortedMonths = sortMonthsDescending(res.data);
    const latestThree = sortedMonths.slice(0, 4);
    setMonths(latestThree);
  };

  const loadGarages = async (customerID) => {
    const res = await axios.get(
      `/invoice/garages/?customer_id=${customerID}`
    );
    setGarages(res.data);
  };



  /* ---------------- PREVIEW PDF ---------------- */

  const previewInvoice = async (month) => {
    if (!selectedCustomer) return;

    if (
      GARAGE_REQUIRED_CUSTOMERS.includes(String(selectedCustomer)) &&
      selectedGarages.length === 0
    ) {
      alert("Please select at least one garage");
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams({
        customer_id: selectedCustomer,
        month: month,
      });

      if (
        GARAGE_REQUIRED_CUSTOMERS.includes(
          String(selectedCustomer)
        )
      ) {
        selectedGarages.forEach((g) =>
          params.append("garage_ids", g)
        );
      }

      const response = await axios.get(
        `/invoice/generate-invoice-comparison-pdf/?${params.toString()}`,
        { responseType: "arraybuffer" }
      );

      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) =>
            data + String.fromCharCode(byte),
          ""
        )
      );

      setPdfUrl(`data:application/pdf;base64,${base64}`);
    } catch (err) {
      console.error("Preview error:", err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Failed to preview PDF";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Approved list of customers ---------------- */


  const loadApprovedInvoiceMonths = async () => {
    try {
      const res = await axios.get(
        "/invoice/approved-invoice-months/"
      );

      setStatusMonths(res.data);
      setAllStatusMonths(res.data); // Store all months
    } catch (err) {
      console.error("Failed to load invoice months", err);
    }
  };

  const loadApprovedInvoices = async (month) => {
    if (!month) return;

    try {
      const res = await axios.get(
        `/invoice/approved-invoices/?month=${month}`  
      );

      setApprovedInvoices(res.data);
    } catch (err) {
      console.error("Failed to load approved invoices", err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Failed to load approved invoices";
      alert(errorMessage);
    }
  };

  // const uniqueStatusMonths = [
  //   ...new Set(approvedInvoices.map((inv) => inv.month_year)),
  // ].sort((a, b) => new Date(b) - new Date(a));

  // const filteredInvoices = approvedInvoices.filter(
  //   (inv) => inv.month_year === statusFilterMonth
  // );

  /* ---------------- Approve PDF ---------------- */

  const handleApproveInvoice = async () => {
    if (!selectedCustomer || !compareMonth) {
      setError("Select customer and month first");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      const selectedCustomerObj = customers.find(
        (c) => String(c.CustomerID) === String(selectedCustomer)
      );

      // const payload = {
      //   customer_name: selectedCustomerObj?.CustomerName,
      //   month: compareMonth,
      //   garage_ids:
      //     GARAGE_REQUIRED_CUSTOMERS.includes(String(selectedCustomer))
      //       ? selectedGarages
      //       : null,
      // };

      // const response = await axios.post(
      //   `${BASE_URL}/invoice/save-invoice/`,
      //   payload
      // );

      const payload = {
        customer_id: selectedCustomer,
        customer_name: selectedCustomerObj?.CustomerName,
        month: compareMonth,
        garage_ids:
          GARAGE_REQUIRED_CUSTOMERS.includes(String(selectedCustomer))
            ? selectedGarages
            : null,
      };

      const response = await axios.post(
        "/invoice/save-invoice/",
        payload
      );

      setMessage(response.data.message || "Invoice Approved Successfully");
      loadApprovedInvoices(statusFilterMonth); // refresh list

      // optional auto hide
      setTimeout(() => {
        setMessage(null);
      }, 4000);

    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Failed to approve invoice");
      }

      setTimeout(() => {
        setError(null);
      }, 5000);

    } finally {
      setLoading(false);
    }
  };



  const handleRejectInvoice = async () => {
    if (!selectedCustomer || !compareMonth) {
      setError("Select customer and month first");
      return;
    }

    if (!rejectionReason.trim()) {
      setError("Please provide reason for rejection");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      const selectedCustomerObj = customers.find(
        (c) => String(c.CustomerID) === String(selectedCustomer)
      );

      const payload = {
        customer_id: selectedCustomer,
        customer_name: selectedCustomerObj?.CustomerName,
        month: compareMonth,
        garage_ids:
          GARAGE_REQUIRED_CUSTOMERS.includes(String(selectedCustomer))
            ? selectedGarages
            : null,
        status: "Rejected",
        description: rejectionReason,
      };

      const response = await axios.post(
        "/invoice/save-invoice/",
        payload
      );

      setMessage(response.data.message || "Invoice Rejected Successfully");

      setShowRejectBox(false);
      setRejectionReason("");

      setTimeout(() => {
        setMessage(null);
      }, 4000);

    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Failed to reject invoice");
      }

      setTimeout(() => {
        setError(null);
      }, 5000);

    } finally {
      setLoading(false);
    }
  };



  /* ---------------- DOWNLOAD PDF ---------------- */

  const handleCompareInvoices = async () => {
    if (!selectedCustomer || !compareMonth) {
      alert("Please select a month");
      return;
    }

    if (
      GARAGE_REQUIRED_CUSTOMERS.includes(String(selectedCustomer)) &&
      selectedGarages.length === 0
    ) {
      alert("Please select at least one garage");
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams({
        customer_id: selectedCustomer,
        month: compareMonth,
      });

      if (
        GARAGE_REQUIRED_CUSTOMERS.includes(
          String(selectedCustomer)
        )
      ) {
        selectedGarages.forEach((g) =>
          params.append("garage_ids", g)
        );
      }

      const response = await axios.get(
        `/invoice/generate-invoice-comparison-pdf/?${params.toString()}`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Invoice_Comparison.pdf");

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err.response?.data?.detail);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Failed to download PDF";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  /* ----------------Email PREVIEW  ---------------- */


  const openEmailPreview = async (invoice) => {
    try {
      const res = await axios.get(
        `/customers/${invoice.customer_id}`
      );

      setRepresentativeInfo(res.data);
      setSelectedInvoice(invoice);
      setEmailPreviewOpen(true);

    } catch (err) {
      console.error("Failed to load customer info", err);
      alert("Unable to load customer information.");
    }
  };


  const sendInvoiceEmail = async (invoice) => {
    try {
      setSendingEmail(true);

      const params = new URLSearchParams();

      params.append("customer_id", invoice.customer_id);
      params.append("month", invoice.month_year);

      if (invoice.garage_ids) {
        invoice.garage_ids.forEach((garage) => {
          params.append("garage_ids", garage);
        });
      }

      await axios.post(
        `/invoice/send-invoice-email/?${params.toString()}`
      );



      // Close modal
      setEmailPreviewOpen(false);

      // Clear selected invoice
      setSelectedInvoice(null);

      // refresh invoice list
      loadApprovedInvoices(statusFilterMonth);

    } catch (error) {
      console.error(error);
      alert("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    loadCustomers();
    loadApprovedInvoiceMonths();
  }, []);

  useEffect(() => {
    if (!selectedCustomer) return;

    setSelectedGarages([]);
    setCompareMonth("");
    setPdfUrl(null);

    loadMonths(selectedCustomer);

    if (
      GARAGE_REQUIRED_CUSTOMERS.includes(
        String(selectedCustomer)
      )
    ) {
      loadGarages(selectedCustomer);
    } else {
      setGarages([]);
    }
  }, [selectedCustomer]);

  // for text display (o) January 2026    (This will compare invoice with the month of December 2025)
  const getPreviousMonth = (monthStr) => {
    const date = new Date(monthStr);
    date.setMonth(date.getMonth() - 1);

    return date.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  };

  /* ---------------- YEAR/MONTH FILTER LOGIC ---------------- */

  // Get unique years from ALL statusMonths (not filtered ones)
  const availableYears = [...new Set(
    allStatusMonths
      .map(month => {
        try {
          const date = new Date(month);
          const year = date.getFullYear();
          return isNaN(year) ? null : year.toString();
        } catch {
          return null;
        }
      })
      .filter(year => year !== null && year !== "NaN")
  )].sort((a, b) => b - a); // Sort descending

  // Load months for selected year using existing API
  const loadMonthsForYear = async (year) => {
    if (!year || year === "NaN") return [];

    try {
      const res = await axios.get(
        `/invoice/approved-invoices/?year=${year}`
      );
      //  array of months ["2026-01-01", "2026-02-01", ...]
      return res.data;
    } catch (err) {
      console.error("Failed to load months for year", err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message;
      if (errorMessage) {
        alert(errorMessage);
      }
      return [];
    }
  };

  // Filter months by selected year (for month dropdown)
  const monthsForSelectedYear = statusMonths.filter(month => {
    if (!statusFilterYear) return false;
    const date = new Date(month);
    return date.getFullYear().toString() === statusFilterYear;
  });

  /* ---------------- UI ---------------- */

  return (
    <div className="p-1">
      <div className="bg-white shadow rounded-xl p-5 border">

        {!showStatusTable && (
          <>
            {/*  TOP SECTION (HIDES WHEN EXPANDED) */}
            {!isExpanded && (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                <RiFileCheckFill /> Approved Invoices
              </h2>

              {!showStatusTable && statusMonths.length > 0 && (
                <button
                  onClick={() => {
                    setShowStatusTable(true);
                    setIsExpanded(false);
                  }}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 bg-gray-200 px-3 py-2 rounded hover:bg-gray-300 transition"
                  title="Check Invoice Status"
                >
                  <FiEye />
                  Check Status
                </button>
              )}
            </div>

            {/* CUSTOMER */}
            <div className="mt-4">
              <label className="font-semibold">Customer</label>
              <select
                className="border rounded p-2 w-full mt-1"
                value={selectedCustomer}
                onChange={(e) =>
                  setSelectedCustomer(e.target.value)
                }
              >
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option
                    key={c.CustomerID}
                    value={c.CustomerID}
                  >
                    {c.CustomerName}
                  </option>
                ))}
              </select>
            </div>

            {/* GARAGES */}
            {GARAGE_REQUIRED_CUSTOMERS.includes(
              String(selectedCustomer)
            ) &&
              garages.length > 0 && (
                <div className="mt-4">
                  <label className="font-semibold block mb-2">
                    Select Garages
                  </label>

                  <div className="grid grid-cols-2 gap-2 border p-3 rounded max-h-48 overflow-y-auto">
                    {garages.map((g) => {
                      const disabled =
                        isParkingConcept &&
                        ((isGroup1Garage(g.garage_id) &&
                          hasGroup2Selected) ||
                          (!isGroup1Garage(g.garage_id) &&
                            hasGroup1Selected));

                      return (
                        <label
                          key={g.garage_id}
                          className={`flex gap-2 ${disabled ? "opacity-50" : ""}`}
                        >
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={selectedGarages.includes(g.garage_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedGarages((prev) => [
                                  ...prev,
                                  g.garage_id,
                                ]);
                              } else {
                                setSelectedGarages((prev) =>
                                  prev.filter(
                                    (id) => id !== g.garage_id
                                  )
                                );
                              }
                            }}
                          />
                          {g.garage_name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* MONTH SELECTOR */}
            {months.length > 0 && (
              <div className="mt-4">
                <label className="font-semibold block mb-2">
                  Select Month
                </label>

                {months.map((m) => (
                  <label key={m} className="flex items-center gap-2 mb-1">
                    <input
                      type="radio"
                      name="compareMonth"
                      value={m}
                      checked={compareMonth === m}
                      onChange={() => {
                        setCompareMonth(m);
                        setIsExpanded(true);
                        previewInvoice(m);
                      }}
                    />

                    <span className="font-medium">{m}</span>

                    <span className="text-sm text-red-500 ml-2">
                      (This will compare invoice with the month of{" "}
                      {getPreviousMonth(m)})
                    </span>
                  </label>
                ))}

                {/* DOWNLOAD BUTTON */}
                <button
                  className="mt-3 bg-green-500 text-white px-4 py-2 rounded"
                  disabled={!compareMonth}
                  onClick={handleCompareInvoices}
                >
                  {loading ? "Generating..." : "Download PDF"}
                </button>

                {/*  ZOOM ICON  */}
                {pdfUrl && (
                  <div className="mt-2">
                    <button
                      onClick={() =>
                        setIsExpanded((prev) => !prev)
                      }
                      className="flex items-center gap-2 text-gray-700 hover:text-black transition"
                      title={isExpanded ? "Back" : "Expand"}
                    >
                      {isExpanded ? (
                        <>
                          <RiArrowLeftLine className="text-2xl" />
                          <span className="font-medium">Back</span>
                        </>
                      ) : (
                        <RiZoomInLine className="text-2xl" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
              </>
            )}

        {/* PDF PREVIEW */}
        {pdfUrl && (
          // <div className={isExpanded ? "mt-0" : "mt-2"}>
          <div className={isExpanded ? "mt-0" : "mt-2"}>

            {isExpanded && (
              <div className="mb-2">
                <button
                  onClick={() =>
                    setIsExpanded((prev) => !prev)
                  }
                  className="flex items-center gap-2 text-gray-700 hover:text-black transition"
                  title="Back"
                >
                  <RiArrowLeftLine className="text-2xl" />
                  <span className="font-medium">Back</span>
                </button>
              </div>
            )}

            {/* PDF */}
            <div className="bg-white rounded shadow">
              <embed
                src={`${pdfUrl}#zoom=135`}
                title="Invoice Preview"
                width="100%"
                height={isExpanded ? "700px" : "700px"}
              />
            </div>

            {/* APPROVE SECTION (ALWAYS VISIBLE) */}
            <div className="mt-4">

              {error && (
                <p className="mb-3 text-sm text-red-600 bg-red-100 border border-red-200 p-3 rounded">
                  {error}
                </p>
              )}

              {message && (
                <p className="mb-3 text-sm text-green-700 bg-green-100 border border-green-200 p-3 rounded">
                  {message}
                </p>
              )}

              {/*  REJECTION TEXTAREA */}
              {showRejectBox && (
                <div className="mb-4">
                  <label className="block font-semibold mb-2 text-red-600">
                    Reason for Rejection
                  </label>
                  <textarea
                    className="w-full border rounded p-2"
                    rows="3"
                    placeholder="Enter reason for rejection..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                </div>
              )}

              {/* BUTTONS */}
              <div className="flex space-x-3 justify-end">

                {showRejectBox ? (
                  <button
                    onClick={handleRejectInvoice}
                    disabled={loading}
                    className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 transition"
                  >
                    {loading ? "Rejecting..." : "Confirm Reject"}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowRejectBox(true)}
                    disabled={loading}
                    className="bg-red-300 text-black px-6 py-2 rounded hover:bg-red-400 transition"
                  >
                    Reject Invoice
                  </button>
                )}

                <button
                  onClick={handleApproveInvoice}
                  disabled={loading}
                  className="bg-green-300 text-black px-6 py-2 rounded hover:bg-green-400 transition"
                >
                  {loading ? "Approving..." : "Approve Invoice"}
                </button>

              </div>

            </div>

          </div>
        )}
      </>
    )}

        {/* APPROVED / REJECTED LIST */}
        {showStatusTable && statusMonths.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <RiFileCheckFill /> Invoice Status
              </h2>
              <button
                onClick={() => setShowStatusTable(false)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 bg-gray-200 px-3 py-2 rounded hover:bg-gray-300 transition"
                title="Back to Comparison"
              >
                <RiArrowLeftLine />
                Back
              </button>
            </div>

            {/* YEAR AND MONTH FILTER */}
            <div className="mb-4 flex gap-4">
              <div>
                <label className="font-semibold mr-2">
                  Select Year:
                </label>
                <select
                  className="border rounded p-2"
                  value={statusFilterYear}
                  onChange={async (e) => {
                    const year = e.target.value;
                    if (!year || year === "NaN") {
                      setStatusFilterYear("");
                      setStatusFilterMonth("");
                      setApprovedInvoices([]);
                      // Restore all months when clearing year selection
                      setStatusMonths(allStatusMonths);
                      return;
                    }
                    
                    setStatusFilterYear(year);
                    setStatusFilterMonth(""); // Reset month when year changes
                    setApprovedInvoices([]); // Clear table

                    // Load months
                    const monthsData = await loadMonthsForYear(year);
                    // Update statusMonths with months for this year only
                    setStatusMonths(monthsData);
                  }}
                >
                  <option value="">Select Year</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {statusFilterYear && (
                <div>
                  <label className="font-semibold mr-2">
                    Select Month:
                  </label>
                  <select
                    className="border rounded p-2"
                    value={statusFilterMonth}
                    onChange={(e) => {
                      const month = e.target.value;
                      setStatusFilterMonth(month);
                      loadApprovedInvoices(month);
                    }}
                  >
                    <option value="">Select Month</option>
                    {monthsForSelectedYear.map((month) => (
                      <option key={month} value={month}>
                        {formatMonthYear(month)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* TABLE ONLY AFTER MONTH SELECTED */}
            {statusFilterMonth && (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2">Customer ID</th>
                      <th className="border p-2">Customer Name</th>
                      <th className="border p-2">Month</th>
                      <th className="border p-2">Garages ID</th>
                      <th className="border p-2">Status</th>
                      <th className="border p-2">Description</th>
                      <th className="border p-2">Email Status</th>
                      <th className="border p-2">Email Sent At</th>
                      <th className="border p-2">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {approvedInvoices.map((inv, index) => (
                      <tr key={index} className="text-center">
                        <td className="border p-2">{inv.customer_id}</td>

                        <td className="border p-2">{inv.customer_name}</td>

                        <td className="border p-2">{inv.month_year}</td>

                        <td className="border p-2">
                          {inv.garage_ids ? inv.garage_ids.join(", ") : "-"}
                        </td>

                        <td
                          className={`border p-2 font-semibold ${inv.status === "Approved"
                              ? "text-green-600"
                              : "text-red-600"
                            }`}
                        >
                          {inv.status}
                        </td>

                        <td className="border p-2">
                          {inv.status === "Rejected"
                            ? inv.description
                            : "-"}
                        </td>
                        <td className="border p-2 font-semibold">
                          {inv.email_status === "Sent" && (
                            <span className="text-green-600">Sent</span>
                          )}

                          {inv.email_status === "Pending" && (
                            <span className="text-gray-500">Pending</span>
                          )}

                          {inv.email_status === "Failed" && (
                            <span className="text-red-600">Failed</span>
                          )}
                        </td>

                        <td className="border p-2">
                          {inv.email_sent_at
                            ? new Date(inv.email_sent_at).toLocaleString()
                            : "-"}
                        </td>

                        {/* ACTION BUTTON */}
                        <td className="border p-2">
                          {inv.status === "Approved" ? (
                            <button
                              onClick={() => openEmailPreview(inv)}
                              disabled={sendingEmail || inv.email_status === "Sent"}
                              className="flex items-center gap-2 mx-auto bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              <FiMail />
                              {inv.email_status === "Sent" ? "Email Sent" : "Send Email"}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {emailPreviewOpen && representativeInfo && (
              <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">

                <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">

                  <h2 className="text-xl font-bold mb-4">
                    Invoice Email Preview
                  </h2>

                  <div className="space-y-2">

                    <p>
                      <strong>Name:</strong>{" "}
                      {representativeInfo.RepresentativeName || "N/A"}
                    </p>

                    <p>
                      <strong>Title:</strong>{" "}
                      {representativeInfo.RepresentativeTitle || "N/A"}
                    </p>

                    <p>
                      <strong>Email:</strong>{" "}
                      {representativeInfo.RepresentativeEmail || "N/A"}
                    </p>

                    <p>
                      <strong>Contact:</strong>{" "}
                      {representativeInfo.RepresentativeContact || "N/A"}
                    </p>

                    <p>
                      <strong>Customer:</strong>{" "}
                      {selectedInvoice.customer_name}
                    </p>

                    <p>
                      <strong>Month:</strong>{" "}
                      {selectedInvoice.month_year}
                    </p>

                  </div>

                  <div className="flex justify-end gap-3 mt-6">

                    <button
                      onClick={() => setEmailPreviewOpen(false)}
                      disabled={sendingEmail}
                      className="px-4 py-2 bg-gray-400 text-white rounded disabled:opacity-50"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() => sendInvoiceEmail(selectedInvoice)}
                      disabled={sendingEmail}
                      className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2 disabled:opacity-50"
                    >
                      {sendingEmail ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : (
                        "Confirm Send"
                      )}
                    </button>

                  </div>

                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default ApprovedInvoicesTab;
